import React, { useEffect, useState } from "react";
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getExpandedRowModel,
    flexRender,
    ExpandedState,
    OnChangeFn,
} from "@tanstack/react-table";
import UpArrow from "../../../assets/svg/upward-arrow.svg";
import DownArrow from "../../../assets/svg/downward-arrow.svg";
import RecipientDetails from "../../../pages/pickRecipient/RecipientDetails";
import { deleteRecipient, getRecipientProfile } from "../../../services/recipientDetails";
import { useNavigate } from "react-router-dom";

interface Data {
    id: number;
    name: string;
    age: number;
    occupation: string;
}

interface TableWithDetailPanelProps {
    data: any;
    handleSelected?: (id: any) => void;
    showToast?: (message: string, type: "success" | "failure") => void;
    setRecipientInfo: React.Dispatch<React.SetStateAction<any>>
    selectedRecipient: any
    setSelectedRecipient: React.Dispatch<any>
    collapseTrigger?: boolean
}

const columnHelper = createColumnHelper<any>();

const columns = [
    columnHelper.accessor("name", {
        header: "Recipient Name",
        enableSorting: true,
    }),
    columnHelper.accessor("bankAccountCountry", {
        header: "Recipient Bank Country",
        enableSorting: true,

    }),
    columnHelper.accessor("accNumber", {
        header: "Recipient Ac.No",
        enableSorting: true,
    }),
    columnHelper.accessor("type", {
        header: "Recipient type",
        enableSorting: true,
    }),
    columnHelper.accessor("residentCountry", {
        header: "Recipient Country",
        enableSorting: true,
    }),
];

const TableWithDetailPanel: React.FC<TableWithDetailPanelProps> = ({
    data,
    setRecipientInfo,
    handleSelected,
    selectedRecipient,
    setSelectedRecipient,

    collapseTrigger
}) => {
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [recipientData, setRecipientData] = useState<any>();
    const [selectedRow, setSelectedRow] = React.useState<number | any>();
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const navigate = useNavigate();

    const handleExpandedChange: OnChangeFn<ExpandedState> = (updaterOrValue) => {
        setExpanded((prev) =>
            typeof updaterOrValue === "function"
                ? updaterOrValue(prev)
                : updaterOrValue
        );
    };

    const tableInstance = useReactTable({
        data,
        columns,
        state: {
            expanded,
        },
        onExpandedChange: handleExpandedChange,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
    });

    const { getHeaderGroups, getRowModel } = tableInstance;

    useEffect(() => {
        getRowModel().rows.forEach((row) => {
            if (row.getIsExpanded()) {
                row.toggleExpanded(false);
            }
        });
    }, [collapseTrigger]);

    const getRecepientsDetails = async (recipient: any) => {
        const userID = recipient?.id;
        if (!userID) return;

        try {
            const req = await getRecipientProfile(userID);
            const res = req.data;
            if (res) {
                setRecipientData(res);
            }
        } catch (error) {
            console.error("Failed to fetch recipient profile:", error);
        }
    };

    useEffect(() => {
        const matchedRecipient = data?.find((item: any) => item.id === selectedRow);
        if (matchedRecipient) {
            getRecepientsDetails(matchedRecipient);
        }
    }, [data, selectedRow]);


    const handleDeleteRecipient = () => {
        deleteRecipient(recipientData?.id)
            .then(() => {
                navigate("/pick-recipient");
            })
            .catch((error: any) => {
                console.error(error);
            });
    };

    // useEffect(() => {
    //     const firstRow = getRowModel().rows[0];
    //     if (isInitialLoad && firstRow) {
    //         const clickedRowId = firstRow.original?.id;
    //         setSelectedRecipient(firstRow.original);
    //         setSelectedRow(clickedRowId);
    //         firstRow.toggleExpanded(true);
    //         setRecipientInfo(firstRow.original);
    //         setIsInitialLoad(false);
    //     }
    // }, [getRowModel, isInitialLoad]);

    return (
        <div className="mt-3 mx-[3.25rem]">
            {data.length !== 0 && <table style={{ width: "100%", zIndex: "30" }}>
                <thead>
                    {getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id} >
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    style={{ padding: "10px", textAlign: "justify" }}
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody >
                    {getRowModel().rows.map((row) => (
                        <React.Fragment key={row.id}>
                            <tr
                                className="hover:bg-[#F0F5FF] cursor-pointer overflow-y-scroll"
                                style={
                                    row.getIsExpanded()
                                        ? { backgroundColor: "#F0F5FF" }
                                        : {}
                                }
                                onClick={() => {
                                    const clickedRowId = row?.original?.id;
                                    // If clicking the same row → collapse it
                                    if (selectedRow === clickedRowId) {
                                        row.toggleExpanded(false);
                                        setSelectedRow(undefined);
                                        return;
                                    }

                                    // Collapse any previously expanded row
                                    getRowModel().rows.forEach(r => {
                                        if (r.getIsExpanded()) {
                                            r.toggleExpanded(false);
                                        }
                                    });

                                    // Expand the new row
                                    setSelectedRecipient(row.original);
                                    row.toggleExpanded(true);
                                    setSelectedRow(clickedRowId);
                                    setRecipientInfo(row.original);
                                }}
                            >
                                {row.getVisibleCells().map((cell) => {
                                    return (
                                        <td key={cell.id} style={{ padding: "10px" }}
                                            className="cursor-pointer">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    )
                                }
                                )}
                                <td>
                                    <button
                                        className=":hover:scale-150 transition-transform duration-200 mr-3"
                                    >
                                        <img src={row.getIsExpanded() ? UpArrow : DownArrow} alt="toggle" />
                                    </button>
                                </td>
                            </tr>
                            {row.getIsExpanded() && (
                                <tr>
                                    <td colSpan={columns.length + 1}>
                                        <RecipientDetails
                                            recipientData={recipientData}
                                            deleteRecipientHandler={handleDeleteRecipient}
                                            handleSelectedRecipient={handleSelected}
                                        />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            }
        </div>
    );
};

export default TableWithDetailPanel;
